import { useEffect, useState } from "react";
import { Sparkles, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const SOCIAL_FIELDS = [
  { key: "instagram", label: "Instagram URL" },
  { key: "youtube", label: "YouTube URL" },
  { key: "facebook", label: "Facebook URL" },
  { key: "linkedin", label: "LinkedIn URL" },
];

/**
 * Lead-admin editor for the public brand-story hover panel (landing page
 * logo/brand hover) and the persistent social-icon rail. Backed by
 * GET/PUT /api/brand-profile.
 */
export default function AdminBrandProfilePanel() {
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/brand-profile").then(r => setProfile(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!profile) return null;

  const set = (k, v) => setProfile(prev => ({ ...prev, [k]: v }));
  const setSocial = (k, v) => setProfile(prev => ({ ...prev, social_links: { ...prev.social_links, [k]: v } }));

  const setMilestone = (i, k, v) => {
    const next = [...profile.milestones];
    next[i] = { ...next[i], [k]: v };
    set("milestones", next);
  };
  const addMilestone = () => set("milestones", [...(profile.milestones || []), { year: "", text: "" }]);
  const removeMilestone = (i) => set("milestones", profile.milestones.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/brand-profile", {
        title: profile.title,
        tagline: profile.tagline,
        milestones: profile.milestones,
        closing_note: profile.closing_note || "",
        social_links: profile.social_links || {},
      });
      setProfile(data);
      toast.success("Counseltaria profile updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="card-soft p-8 mb-8" data-testid="admin-brand-profile-panel">
      <h2 className="font-serif-display text-2xl inline-flex items-center gap-2 mb-1">
        <Sparkles className="w-5 h-5" style={{ color: "var(--brand)" }} /> Counseltaria Profile
      </h2>
      <p className="text-xs mb-6" style={{ color: "var(--text-secondary)" }}>
        Powers the "our story" panel visitors see when they hover the logo on the landing page,
        and the social links in the left-side rail shown across the app.
      </p>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="label-eyebrow block mb-2">Story title</label>
          <input data-testid="brand-title-input" value={profile.title} onChange={e => set("title", e.target.value)}
            className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-2.5 focus:outline-none focus:border-[color:var(--brand)]" />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Tagline</label>
          <input data-testid="brand-tagline-input" value={profile.tagline} onChange={e => set("tagline", e.target.value)}
            className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-2.5 focus:outline-none focus:border-[color:var(--brand)]" />
        </div>
      </div>

      <label className="label-eyebrow block mb-2">Milestones (timeline)</label>
      <div className="space-y-3 mb-4" data-testid="brand-milestones">
        {(profile.milestones || []).map((m, i) => (
          <div key={i} className="flex gap-2 items-start" data-testid={`brand-milestone-row-${i}`}>
            <input value={m.year} onChange={e => setMilestone(i, "year", e.target.value)}
              placeholder="Year" data-testid={`brand-milestone-year-${i}`}
              className="w-24 rounded-xl border border-[color:var(--border-soft)] px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--brand)]" />
            <input value={m.text} onChange={e => setMilestone(i, "text", e.target.value)}
              placeholder="What happened" data-testid={`brand-milestone-text-${i}`}
              className="flex-1 rounded-xl border border-[color:var(--border-soft)] px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--brand)]" />
            <button type="button" onClick={() => removeMilestone(i)} data-testid={`brand-milestone-remove-${i}`}
              className="btn-outline text-xs px-2.5 py-2"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
      <button type="button" onClick={addMilestone} data-testid="brand-milestone-add"
        className="btn-outline text-xs mb-6 inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add milestone</button>

      <div className="mb-6">
        <label className="label-eyebrow block mb-2">Closing note</label>
        <textarea data-testid="brand-closing-note-input" rows={2} value={profile.closing_note || ""}
          onChange={e => set("closing_note", e.target.value)}
          className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-2.5 resize-none focus:outline-none focus:border-[color:var(--brand)]" />
      </div>

      <label className="label-eyebrow block mb-2">Social links (left-side rail)</label>
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        {SOCIAL_FIELDS.map(({ key, label }) => (
          <div key={key}>
            <label className="text-xs block mb-1" style={{ color: "var(--text-secondary)" }}>{label}</label>
            <input value={profile.social_links?.[key] || ""} onChange={e => setSocial(key, e.target.value)}
              data-testid={`brand-social-${key}`} placeholder="https://…"
              className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-2.5 text-sm focus:outline-none focus:border-[color:var(--brand)]" />
          </div>
        ))}
      </div>

      <button data-testid="brand-profile-save" onClick={save} disabled={busy}
        className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed">
        {busy ? "Saving…" : "Save Counseltaria profile"}
      </button>
    </div>
  );
}
